import { useEffect, useState } from 'react';
import type { CleaningScopeApartmentResponse } from '@/shared/api';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
  Textarea,
} from '@/shared/components';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (vars: { propertyId: string; scheduledFor: string; notes: string | null }) => void;
  isPending: boolean;
  /** Die Apartments des gewählten Objekts — dieselben, die die Liste zeigt. */
  apartments: CleaningScopeApartmentResponse[];
  /** Der Tag der Liste, in Ortszeit des Objekts. */
  date: string;
  /** Ohne Netz ginge nichts hinaus; Anlegen ist dann gesperrt. */
  offline: boolean;
}

/**
 * Sonderreinigung ohne Buchung. Apartments und Tag kommen aus der Liste (Objekt und Ortszeit), statt
 * aus einer eigenen Abfrage aller Apartments; die Notiz sieht die Reinigung als „Notiz der Leitung“.
 */
export function ManualTaskModal({
  open,
  onOpenChange,
  onCreate,
  isPending,
  apartments,
  date,
  offline,
}: Props) {
  const [propertyId, setPropertyId] = useState('');
  const [scheduledFor, setScheduledFor] = useState(date);
  const [notes, setNotes] = useState('');

  // Jedes Öffnen beginnt leer am Tag der Liste: der Dialog bleibt eingehängt, und ein altes
  // Apartment aus einem anderen Objekt stünde sonst unsichtbar in der Auswahl.
  useEffect(() => {
    if (!open) return;
    setPropertyId('');
    setScheduledFor(date);
    setNotes('');
  }, [open, date]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reinigung außerhalb der Reihe</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-4">
            <FormField label="Apartment" required>
              <Select value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
                <option value="">Apartment wählen</option>
                {apartments.map((apartment) => (
                  <option key={apartment.id} value={apartment.id}>
                    {apartment.internalName ?? apartment.name}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Tag" required>
              <Input
                type="date"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
              />
            </FormField>
            <FormField
              label="Notiz der Leitung"
              hint="Optional – die Reinigung sieht sie in der Aufgabe."
            >
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="z. B. Tiefenreinigung Bad, Polstermöbel saugen"
              />
            </FormField>
            <p className="text-ink-soft text-xs">
              Nach jeder Abreise legt StayOn die Reinigung selbst an. Das hier ist für
              Sonderreinigungen ohne Buchung; Fotos verlangt sie, wenn die Raumliste des Apartments
              Fotos verlangt.
            </p>
          </div>
        </DialogBody>
        <DialogFooter>
          {offline && (
            <p role="status" className="text-ink-soft mr-auto self-center text-xs">
              Keine Verbindung – Anlegen geht wieder mit Netz.
            </p>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
            Abbrechen
          </Button>
          <Button
            variant="brand"
            disabled={!propertyId || !scheduledFor || isPending || offline}
            loading={isPending}
            onClick={() => onCreate({ propertyId, scheduledFor, notes: notes.trim() || null })}
          >
            Anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
